import {
  DocumentNode,
  OperationVariables,
  TypedDocumentNode,
  useApolloClient,
} from '@apollo/client'
import { useEffect, useState } from 'react'

export type UseManualSubscriptionConfig<
  Data,
  Variables extends OperationVariables,
> = {
  query: DocumentNode | TypedDocumentNode<Data, Variables>
  variables: Variables
  skip?: boolean
  // identifies when `variables` meaningfully changed, since object identity changes every render
  restartKey: string
}

// Apollo's client.subscribe() keeps the variables it was opened with, so callers must
// pass a `restartKey` that changes whenever the subscription should be torn down and reopened.
export default function useManualSubscription<
  Data,
  Variables extends OperationVariables,
>({
  query,
  variables,
  skip = false,
  restartKey,
}: UseManualSubscriptionConfig<Data, Variables>) {
  const client = useApolloClient()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [data, setData] = useState<Data | undefined | null>()

  useEffect(() => {
    if (skip) return

    setLoading(true)
    const subscription = client
      .subscribe<Data, Variables>({ query, variables })
      .subscribe({
        next(result) {
          setData(result.data)
          setLoading(false)
        },
        error(err) {
          // eslint-disable-next-line no-console
          console.error('useManualSubscription error', err)
          setError(err)
          setLoading(false)
        },
      })

    return () => {
      if (!subscription.closed) {
        subscription.unsubscribe()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, skip, restartKey])

  return { data, loading, error }
}
